import { useEffect, useState } from 'react';
import { App, Input, Modal, Segmented, Typography } from 'antd';
import type { ButtonProps } from 'antd';
import { normalizeApiError } from '@/api/errors';

const { Paragraph } = Typography;

export type CaseActionRequest =
  | { kind: 'override'; title: string; run: (finding: 'ACCEPTED' | 'REJECTED', reason: string) => Promise<unknown> }
  | { kind: 'approve-override'; title: string; run: (note: string) => Promise<unknown> }
  | { kind: 'reject-override'; title: string; run: (note: string) => Promise<unknown> }
  | { kind: 'redo'; title: string; run: (reason: string) => Promise<unknown> }
  | { kind: 'rerequest'; title: string; run: (reason: string, applicantMessage: string) => Promise<unknown> };

const INTRO: Record<CaseActionRequest['kind'], string> = {
  override:
    'Proposes your finding on this evidence. A second reviewer must approve it before it takes effect. The evidence and the run outcome stay as they are, and your name and reason go in the decision history.',
  'approve-override':
    'Puts this finding in force, under both your names. The evidence and the run outcome stay as they are. You cannot approve your own proposal.',
  'reject-override': 'Turns this proposal down. No finding takes effect, and your name and reason go in the decision history.',
  redo: 'Runs this step again and adds its new evidence to the case. The run itself does not change route.',
  rerequest: 'Reopens this step for the applicant, on the link they already have.',
};

/** Every case action needs a reason, bar approving someone else's: it is what the decision history is for. */
export function CaseActionModal({
  request,
  onClose,
  onDone,
}: {
  request: CaseActionRequest | null;
  onClose: () => void;
  onDone: () => void;
}) {
  const { message } = App.useApp();
  const [reason, setReason] = useState('');
  const [finding, setFinding] = useState<'ACCEPTED' | 'REJECTED'>('REJECTED');
  const [applicantMessage, setApplicantMessage] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    setReason('');
    setFinding('REJECTED');
    setApplicantMessage('');
  }, [request]);

  async function submit() {
    if (!request) return;
    setBusy(true);
    try {
      if (request.kind === 'override') await request.run(finding, reason.trim());
      else if (request.kind !== 'rerequest') await request.run(reason.trim());
      else await request.run(reason.trim(), applicantMessage.trim());
      onDone();
      onClose();
    } catch (e) {
      message.error(normalizeApiError(e).message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal
      open={request !== null}
      title={request?.title}
      okText={request?.kind === 'override' ? 'Propose' : request?.kind === 'approve-override' ? 'Approve' : request?.kind === 'reject-override' ? 'Reject' : 'Record'}
      onOk={() => void submit()}
      onCancel={onClose}
      okButtonProps={{ disabled: request?.kind !== 'approve-override' && reason.trim().length < 3, loading: busy, 'data-testid': 'case-action-submit' } as ButtonProps}
      destroyOnClose
    >
      {request && <Paragraph style={{ fontSize: 12.5 }}>{INTRO[request.kind]}</Paragraph>}
      {request?.kind === 'override' && (
        <div style={{ marginBottom: 12 }}>
          <Segmented
            value={finding}
            onChange={(v) => setFinding(v as 'ACCEPTED' | 'REJECTED')}
            options={[
              { label: 'Reject this evidence', value: 'REJECTED' },
              { label: 'Accept this evidence', value: 'ACCEPTED' },
            ]}
          />
        </div>
      )}
      <label style={{ display: 'block', fontSize: 12.5, marginBottom: 4 }} htmlFor="case-action-reason">
        {request?.kind === 'approve-override' ? 'Note (optional, internal)' : 'Reason (internal)'}
      </label>
      <Input.TextArea
        id="case-action-reason"
        rows={3}
        maxLength={2000}
        value={reason}
        onChange={(e) => setReason(e.target.value)}
        data-testid="case-action-reason"
      />
      {request?.kind === 'rerequest' && (
        <>
          <label style={{ display: 'block', fontSize: 12.5, margin: '12px 0 4px' }} htmlFor="case-action-message">
            Message to the applicant (optional)
          </label>
          <Input.TextArea
            id="case-action-message"
            rows={2}
            maxLength={500}
            placeholder="For example: the photo was blurred, please take it again."
            value={applicantMessage}
            onChange={(e) => setApplicantMessage(e.target.value)}
            data-testid="case-action-message"
          />
          <Paragraph style={{ fontSize: 11.5, marginTop: 4, color: 'var(--ol-text-secondary)' }}>
            The applicant sees this exactly as written. Do not mention risk findings or checks.
          </Paragraph>
        </>
      )}
    </Modal>
  );
}
